import { Component } from 'js-component-framework';
import fastdom from 'fastdom';

/**
 * CounterBox component.
 */
class CounterBox extends Component {
  /**
   * Create a new CounterBox.
   *
   * @constructor
   */
  constructor(config) {
    super(config);

    this.init = this.init.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleAnimation = this.handleAnimation.bind(this);

    /**
     * The target value.
     *
     * @type {Number}
     */
    this.value = Number(this.element.getAttribute('data-target'));

    /**
     * The current number in the increment, starting at 0.
     *
     * @type {Number}
     */
    this.number = 0;

    const noPreference = (
      window.matchMedia('(prefers-reduced-motion: no-preference)')
    );

    // Only animate the counter if the user has no `prefers-reduced-motion` preference.
    if (noPreference.matches) {
      // Reset the value to 0.
      this.element.innerText = '0';

      this.init();
    }
  }

  init() {
    /**
     * Intersection Observer for starting animation when the component is fully visible.
     *
     * @type {IntersectionObserver}
     */
    this.observer = new IntersectionObserver(
      this.handleIntersection,
      { threshold: 1 },
    );

    // Observe the element.
    this.observer.observe(this.element);
  }

  /**
   * Handle IntersectionObserver changes.
   *
   * @param {IntersectionObserverEntry} entries The IntersectionObserver entries.
   */
  handleIntersection(entries) {
    const [entry] = entries;

    if (entry.isIntersecting) {
      this.handleAnimation();

      // No need to observe the element anymore.
      this.observer.unobserve(this.element);
    }
  }

  /**
   * Animates the box counter.
   */
  handleAnimation() {
    // Allow for 200 animations.
    const increment = (this.value * 0.005);

    if (this.number < this.value) {
      // Get the next number value.
      this.number = Number(this.number + increment);

      // Update the counter in the DOM.
      fastdom.mutate(() => {
        this.element.innerText = (
          Number.parseInt(this.number, 10).toLocaleString()
        );
      });

      // Continue the animation.
      setTimeout(this.handleAnimation, 1);
    } else {
      // Update the counter in the DOM.
      fastdom.mutate(() => {
        this.element.innerText = Number(this.value).toLocaleString();
      });
    }
  }
}

const counterBoxConfig = {
  name: 'counter-box',
  class: CounterBox,
};

export default counterBoxConfig;
